import { useEffect, useState } from "react";
import { Loader2, Lock, X } from "lucide-react";
import { AwpRuleSet, RULE_LAYERS, RuleLayer, RuleSetEvent, listRuleSetAudit, updateRuleSet } from "./rulesApi";

interface UnlockDialogProps {
  set: AwpRuleSet;
  onCancel: () => void;
  /** Resolves when the unlock has been saved; rejects with the reason it was refused. */
  onUnlock: (reason: string) => Promise<void>;
}

/**
 * Unlocking an active standard asks why. It reopens rules projects may already be checked against,
 * so the reason is required and is written to the standard's audit trail with who gave it.
 */
export const UnlockDialog = ({ set, onCancel, onUnlock }: UnlockDialogProps) => {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!reason.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onUnlock(reason.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not unlock the standard.");
      setSaving(false);
    }
  };

  return (
    <div className="vr-backdrop" role="dialog" aria-modal="true" aria-label="Unlock standard">
      <div className="vr-modal vr-modal-sm">
        <div className="vr-modal-head">
          <div>
            <h3>Unlock this standard?</h3>
            <p>{set.title}</p>
          </div>
          <button className="vr-iconbtn" onClick={onCancel} aria-label="Close" disabled={saving}>
            <X size={16} />
          </button>
        </div>
        <div className="vr-modal-body">
          <p className="vr-field-hint" style={{ fontSize: ".8rem" }}>
            It goes back to Draft so its rules can be changed. The reason is recorded on the audit trail
            with your name.
          </p>
          <div className="vr-field">
            <label htmlFor="vr-unlock-reason">Reason for unlocking</label>
            <textarea
              id="vr-unlock-reason"
              className="vr-input vr-textarea"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Correcting GEN-014 severity per client comment"
              autoFocus
            />
          </div>
          {error && <div className="vr-form-error">{error}</div>}
        </div>
        <div className="vr-modal-foot">
          <button className="vr-btn vr-btn-ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button className="vr-btn vr-btn-glow" onClick={() => void submit()} disabled={!reason.trim() || saving}>
            {saving ? <Loader2 size={14} className="vr-spin-inline" /> : <Lock size={14} />} Unlock
          </button>
        </div>
      </div>
    </div>
  );
};

interface EditDetailsDialogProps {
  set: AwpRuleSet;
  onCancel: () => void;
  onSaved: (set: AwpRuleSet) => void;
}

/**
 * Edit what was entered on the upload screen — title, document number, version, layer — without
 * re-importing. Saved against the version the form was opened on; every change lands on the trail.
 */
export const EditDetailsDialog = ({ set, onCancel, onSaved }: EditDetailsDialogProps) => {
  const [title, setTitle] = useState(set.title);
  const [docNo, setDocNo] = useState(set.docNo ?? "");
  const [version, setVersion] = useState(set.version ?? "");
  const [layer, setLayer] = useState<RuleLayer>(set.layer);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!title.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      onSaved(
        await updateRuleSet(set.id, {
          title: title.trim(),
          docNo: docNo.trim(),
          version: version.trim(),
          layer,
          _etag: set._etag,
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the details.");
      setSaving(false);
    }
  };

  return (
    <div className="vr-backdrop" role="dialog" aria-modal="true" aria-label="Edit standard">
      <div className="vr-modal vr-modal-sm">
        <div className="vr-modal-head">
          <div>
            <h3>Edit Standard</h3>
            <p>{set.source.fileName}</p>
          </div>
          <button className="vr-iconbtn" onClick={onCancel} aria-label="Close" disabled={saving}>
            <X size={16} />
          </button>
        </div>
        <div className="vr-modal-body">
          <div className="vr-field">
            <label htmlFor="vr-es-title">Title</label>
            <input id="vr-es-title" className="vr-input" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
          </div>
          <div className="vr-field-grid">
            <div className="vr-field">
              <label htmlFor="vr-es-no">Document No.</label>
              <input id="vr-es-no" className="vr-input" value={docNo} onChange={(e) => setDocNo(e.target.value)} />
            </div>
            <div className="vr-field">
              <label htmlFor="vr-es-ver">Version</label>
              <input id="vr-es-ver" className="vr-input" value={version} onChange={(e) => setVersion(e.target.value)} />
            </div>
          </div>
          <div className="vr-field">
            <label htmlFor="vr-es-layer">Layer</label>
            <select
              id="vr-es-layer"
              className="vr-select"
              value={layer}
              onChange={(e) => setLayer(e.target.value as RuleLayer)}
            >
              {RULE_LAYERS.map((l) => (
                <option key={l.key} value={l.key}>
                  {l.label}
                </option>
              ))}
            </select>
          </div>
          {error && <div className="vr-form-error">{error}</div>}
        </div>
        <div className="vr-modal-foot">
          <button className="vr-btn vr-btn-ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button className="vr-btn vr-btn-glow" onClick={() => void save()} disabled={!title.trim() || saving}>
            {saving ? (
              <>
                <Loader2 size={14} className="vr-spin-inline" /> Saving…
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/** The standard's audit trail, newest first: who imported, activated, unlocked (and why), edited. */
export const RuleSetAuditPanel = ({ set, onClose }: { set: AwpRuleSet; onClose: () => void }) => {
  const [events, setEvents] = useState<RuleSetEvent[] | null>(null);
  const [available, setAvailable] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const result = await listRuleSetAudit(set.id);
        if (!live) return;
        setEvents([...result.items].reverse());
        setAvailable(result.available);
      } catch (err) {
        if (!live) return;
        setError(err instanceof Error ? err.message : "Could not load the audit trail.");
        setEvents([]);
      }
    })();
    return () => {
      live = false;
    };
  }, [set.id]);

  return (
    <div className="vr-backdrop" role="dialog" aria-modal="true" aria-label="Audit trail">
      <div className="vr-modal">
        <div className="vr-modal-head">
          <div>
            <h3>Audit trail</h3>
            <p>{set.title}</p>
          </div>
          <button className="vr-iconbtn" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="vr-modal-body">
          {error && <div className="vr-form-error">{error}</div>}
          {events === null && (
            <div className="vr-empty">
              <Loader2 size={16} className="vr-spin-inline" /> Loading…
            </div>
          )}
          {events !== null && events.length === 0 && (
            <div className="vr-empty">
              {available
                ? "Nothing recorded yet."
                : "The audit log store is not set up in this environment (VerityAuditEvents)."}
            </div>
          )}
          {events && events.length > 0 && (
            <div className="vr-awp-audit">
              {events.map((e) => (
                <div key={e.id} className="vr-awp-audit-row">
                  <div className="vr-awp-audit-when">
                    <div>{new Date(e.at).toLocaleString()}</div>
                    <div className="vr-cell-code">{e.actorName || "Unknown"}</div>
                  </div>
                  <div>
                    <div className={e.action === "ruleset.unlocked" ? "vr-warn" : undefined}>{e.summary}</div>
                    {e.detail && (
                      <div className="vr-cell-code">
                        {e.action === "ruleset.unlocked" ? `Reason: ${e.detail}` : e.detail}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
